import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Chip,
  FormHelperText,
  Switch,
  FormControlLabel,
  CircularProgress,
  Tabs,
  Tab,
  Divider,
  Checkbox,
  ListItemText,
  Alert,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Image as ImageIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import productService from "../../services/productService";
import categoryService from "../../services/categoryService";
import attributeService from "../../services/attributeService";
import categoryAttributeService from "../../services/categoryAttributeService";
import ProductImageManager from "../../components/admin/ProductImageManager";
import VariationGenerator from "../../components/admin/products/VariationGenerator";
import { formatCurrency } from "../../utils/formatters";

// Get the backend URL from environment variables or use a default
const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || "";

// Helper function to get direct URL to image
const getDirectImageUrl = (imageUrl) => {
  if (!imageUrl) return "/placeholder.svg";

  // If it's already an absolute URL (like S3), return as is
  if (imageUrl.startsWith("http")) {
    return imageUrl;
  }

  // For local storage, replace the base URL with our direct backend URL
  const urlParts = imageUrl.split("/uploads/");
  if (urlParts.length === 2) {
    return `${BACKEND_URL}/uploads/${urlParts[1]}`;
  }

  return imageUrl;
};

// TabPanel component for the product form
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`product-tabpanel-${index}`}
      aria-labelledby={`product-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const AdminProductsPage = () => {
  const { t } = useTranslation("common");
  const { t: tProducts } = useTranslation("products");
  const navigate = useNavigate();

  // State for products table
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalProducts, setTotalProducts] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");

  // State for product form
  const [openDialog, setOpenDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formTab, setFormTab] = useState(0);
  const [formData, setFormData] = useState({
    id: "",
    name: "",
    sku: "",
    description: "",
    price: "",
    stock: "",
    categoryId: "",
    isActive: true,
    imageUrl: "",
    salePrice: "",
    cost: "",
    weight: "",
    dimensions: "",
    attributes: [],
    productType: "simple",
    parentId: null,
  });
  const [formErrors, setFormErrors] = useState({});

  // State for categories and attributes
  const [categories, setCategories] = useState([]);
  const [allAttributes, setAllAttributes] = useState([]);
  const [categoryAttributes, setCategoryAttributes] = useState([]);
  const [loadingAttributes, setLoadingAttributes] = useState(false);

  // State for variations
  const [variations, setVariations] = useState([]);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // State for loading variations
  const [loadingVariations, setLoadingVariations] = useState(false);

  // Fetch products
  const fetchProducts = async () => {
    setLoading(true);
    try {
      const data = await productService.getProducts({
        page: page + 1,
        limit: rowsPerPage,
        search: searchQuery,
        includeInactive: true,
        detailed: true,
      });
      setProducts(data.products);
      setTotalProducts(data.pagination.totalItems);
    } catch (error) {
      console.error("Error fetching products:", error);
      toast.error(tProducts("admin.list.fetchError"));
    } finally {
      setLoading(false);
    }
  };

  // Fetch categories
  const fetchCategories = async () => {
    try {
      const data = await categoryService.getCategories({
        includeInactive: false,
      });
      setCategories(data);
    } catch (error) {
      console.error("Error fetching categories:", error);
      toast.error(tProducts("admin.list.categoriesFetchError"));
    }
  };

  // Fetch all attributes
  const fetchAttributes = async () => {
    try {
      const data = await attributeService.getAttributes();
      setAllAttributes(data);
    } catch (error) {
      console.error("Error fetching attributes:", error);
      toast.error(tProducts("admin.list.attributesFetchError"));
    }
  };

  // Fetch category attributes
  const fetchCategoryAttributes = async (categoryId) => {
    if (!categoryId) {
      setCategoryAttributes([]);
      return;
    }

    setLoadingAttributes(true);
    try {
      const data = await categoryAttributeService.getCategoryAttributes(
        categoryId
      );
      setCategoryAttributes(data);
    } catch (error) {
      console.error("Error fetching category attributes:", error);
      toast.error(tProducts("admin.list.categoryAttributesFetchError"));
    } finally {
      setLoadingAttributes(false);
    }
  };

  // Fetch variations
  const fetchVariations = async (productId) => {
    try {
      console.log("Fetching variations for product ID:", productId);
      setLoadingVariations(true);
      const variations = await productService.getProductVariations(productId);
      console.log("Fetched variations:", variations);
      setVariations(variations);
    } catch (error) {
      console.error("Error fetching variations:", error);
      toast.error(tProducts("admin.list.fetchVariationsError"));
    } finally {
      setLoadingVariations(false);
    }
  };

  // Initial data loading
  useEffect(() => {
    fetchProducts();
  }, [page, rowsPerPage, searchQuery]);

  useEffect(() => {
    // Fetch these only once on component mount
    fetchCategories();
    fetchAttributes();
  }, []);

  // Load category attributes when category changes
  useEffect(() => {
    const categoryId = formData.categoryId;
    if (categoryId) {
      fetchCategoryAttributes(categoryId);
    } else {
      setCategoryAttributes([]);
    }
  }, [formData.categoryId]);

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle search with debounce
  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setPage(0);
  };

  // Navigate to create product page
  const handleNavigateToCreate = () => {
    navigate("/admin/products/create");
  };

  // Navigate to edit product page
  const handleNavigateToEdit = (productId) => {
    if (!productId) {
      console.error("Invalid product ID for edit:", productId);
      toast.error(tProducts("admin.list.invalidProductId"));
      return;
    }

    console.log("Navigating to edit product with ID:", productId);
    navigate(`/admin/products/edit/${productId}`);
  };

  // Handle opening dialog for creating a new product
  const handleOpenCreateDialog = () => {
    setIsEditing(false);
    setFormTab(0);
    setFormData({
      id: "",
      name: "",
      sku: "",
      description: "",
      price: "",
      stock: "",
      categoryId: "",
      isActive: true,
      imageUrl: "",
      salePrice: "",
      cost: "",
      weight: "",
      dimensions: "",
      attributes: [],
      productType: "simple",
      parentId: null,
    });
    setFormErrors({});
    setOpenDialog(true);
  };

  // Handle creating a variation of an existing product
  const handleCreateVariation = (parentProduct) => {
    setIsEditing(false);
    setFormTab(0);
    setFormData({
      id: "",
      name: `${parentProduct.name} (Variant)`,
      sku: `${parentProduct.sku}-VAR`,
      description: parentProduct.description,
      price: parentProduct.price,
      stock: "0",
      categoryId: parentProduct.categoryId,
      isActive: true,
      imageUrl: "",
      salePrice: "",
      cost: parentProduct.cost || "",
      weight: parentProduct.weight || "",
      dimensions: parentProduct.dimensions || "",
      attributes: [],
      productType: "simple",
      parentId: parentProduct.id,
    });
    setFormErrors({});
    setOpenDialog(true);
  };

  // Open dialog for editing a product
  const handleOpenEditDialog = async (product, loadFullDetails = false) => {
    setIsEditing(true);
    setFormTab(loadFullDetails ? 2 : 0);
    setFormErrors({});

    // First set basic data
    setFormData({
      id: product.id,
      name: product.name,
      sku: product.sku,
      description: product.description || "",
      price: product.price.toString(),
      stock: product.stock.toString(),
      categoryId: product.categoryId,
      isActive: product.isActive,
      imageUrl: product.imageUrl || "",
      salePrice: product.salePrice ? product.salePrice.toString() : "",
      cost: product.cost ? product.cost.toString() : "",
      weight: product.weight ? product.weight.toString() : "",
      dimensions: product.dimensions || "",
      attributes: [], // Will be loaded separately
      productType: product.productType || "simple",
      parentId: product.parentId || null,
    });

    setOpenDialog(true);

    // Fetch category attributes first
    try {
      await fetchCategoryAttributes(product.categoryId);

      // Then fetch product attributes
      const attributes = await productService.getProductAttributes(product.id);

      // Update only the attributes part of the form to avoid full rerender
      setFormData((prev) => ({
        ...prev,
        attributes: attributes.map((attr) => ({
          attributeId: attr.attributeId,
          value: attr.value,
        })),
      }));

      if (loadFullDetails) {
        const fullProduct = await productService.getProductById(product.id);
        setFormData((prev) => ({
          ...prev,
          imageUrl: fullProduct.imageUrl || "",
        }));
      }
    } catch (error) {
      console.error("Error fetching product details:", error);
      toast.error(tProducts("admin.list.fetchDetailError"));
    }
  };

  // Close dialog
  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    // For productType changes, handle special cases
    if (name === "productType") {
      setFormData((prevFormData) => ({
        ...prevFormData,
        [name]: type === "checkbox" ? checked : value,
      }));
    } else {
      setFormData((prevFormData) => ({
        ...prevFormData,
        [name]: type === "checkbox" ? checked : value,
      }));
    }

    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        [name]: null,
      }));
    }
  };

  // Handle attribute value change
  const handleAttributeChange = (attributeId, value) => {
    setFormData((prevFormData) => {
      let updatedAttributes = [...prevFormData.attributes];

      // Find the attribute in allAttributes to get its type
      const attribute = allAttributes.find((attr) => attr.id === attributeId);
      const isMultiselect = attribute?.type === "multiselect";

      // Handle differently based on whether it's a multiselect
      if (isMultiselect && Array.isArray(value)) {
        // Remove any existing entries for this attribute
        updatedAttributes = updatedAttributes.filter(
          (attr) => attr.attributeId !== attributeId
        );

        // Add individual entries for each selected value
        if (value.length > 0) {
          const newEntries = value.map((val) => ({
            attributeId,
            value: val,
          }));
          updatedAttributes = [...updatedAttributes, ...newEntries];
        }
      } else {
        // Regular single value handling
        const existingIndex = updatedAttributes.findIndex(
          (attr) => attr.attributeId === attributeId
        );

        if (existingIndex >= 0) {
          updatedAttributes[existingIndex] = {
            ...updatedAttributes[existingIndex],
            value,
          };
        } else {
          updatedAttributes.push({ attributeId, value });
        }
      }

      return {
        ...prevFormData,
        attributes: updatedAttributes,
      };
    });
  };

  // Handle image change callback from ProductImageManager
  const handleImagesChange = () => {
    // Refresh the product data to get the updated imageUrl
    if (isEditing && formData.id) {
      productService.getProductById(formData.id).then((product) => {
        setFormData((prev) => ({
          ...prev,
          imageUrl: product.imageUrl || "",
        }));
      });
    }
  };

  // Validate form data
  const validateForm = () => {
    const errors = {};

    // Basic required fields for all products
    if (!formData.name) errors.name = tProducts("validation.required");
    if (!formData.sku) errors.sku = tProducts("validation.required");
    if (!formData.categoryId)
      errors.categoryId = tProducts("validation.required");

    // Price is only required for simple products
    if (
      formData.productType === "simple" &&
      (!formData.price || isNaN(parseFloat(formData.price)))
    ) {
      errors.price = tProducts("validation.required");
    }

    // Handle required attributes
    const requiredAttributeErrors = {};

    // Check for required attributes
    categoryAttributes.forEach((catAttr) => {
      // Check if this attribute is required in the category
      const isRequired =
        catAttr.CategoryAttribute?.isRequired ||
        catAttr.categoryAttribute?.isRequired ||
        false;

      // Check if this attribute is variatable
      const isVariantable =
        catAttr.CategoryAttribute?.isVariantable ||
        catAttr.categoryAttribute?.isVariantable ||
        false;

      // For simple products, don't require variatable attributes
      const skipValidation = formData.productType === "simple" && isVariantable;

      // If required and not a variatable attribute for a simple product, validate it
      if (isRequired && !skipValidation) {
        const productAttr = formData.attributes.find(
          (attr) => attr.attributeId === catAttr.id
        );

        if (!productAttr || !productAttr.value) {
          requiredAttributeErrors[catAttr.id] = tProducts(
            "validation.requiredAttribute",
            {
              name: catAttr.name || "Attribute",
            }
          );
        }
      }
    });

    if (Object.keys(requiredAttributeErrors).length > 0) {
      errors.attributes = requiredAttributeErrors;
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) {
      // If there are attribute errors, switch to the attributes tab
      if (formErrors.attributes) {
        setFormTab(1);
      }
      return;
    }

    // Prepare data for API
    const productData = {
      name: formData.name,
      sku: formData.sku,
      description: formData.description,
      categoryId: formData.categoryId,
      isActive: formData.isActive,
      imageUrl: formData.imageUrl,
      attributes: formData.attributes.filter((attr) => attr.value),
      productType: formData.productType || "simple",
      parentId: formData.parentId,
    };

    // Only include these fields for simple products
    if (formData.productType === "simple") {
      productData.price = parseFloat(formData.price) || 0;
      productData.stock = formData.stock ? parseInt(formData.stock) : 0;
      productData.salePrice = formData.salePrice
        ? parseFloat(formData.salePrice)
        : null;
      productData.cost = formData.cost ? parseFloat(formData.cost) : null;
      productData.weight = formData.weight ? parseFloat(formData.weight) : null;
      productData.dimensions = formData.dimensions;
    } else if (formData.productType === "variable") {
      // For variable products, we still need a price but we'll set default values
      productData.price = parseFloat(formData.price) || 0;
      productData.stock = 0; // Stock will be managed at variation level
      productData.salePrice = null;
      productData.cost = formData.cost ? parseFloat(formData.cost) : null;
      productData.weight = 0;
      productData.dimensions = formData.dimensions || "";
    }

    try {
      let savedProduct;

      if (isEditing) {
        savedProduct = await productService.updateProduct(
          formData.id,
          productData
        );
        toast.success(tProducts("admin.list.updateSuccess"));
      } else {
        // For variable products, explicitly set empty variations array to bypass backend validation
        if (productData.productType === "variable") {
          productData.variations = []; // Empty array to satisfy backend validation
        }

        savedProduct = await productService.createProduct(productData);
        toast.success(tProducts("admin.list.createSuccess"));
      }

      // If we just created a variable product, set it in the form data for further editing
      if (
        !isEditing &&
        productData.productType === "variable" &&
        savedProduct
      ) {
        setIsEditing(true);
        setFormData((prev) => ({
          ...prev,
          id: savedProduct.id,
        }));
        setFormTab(3); // Switch to variations tab after creating
        fetchVariations(savedProduct.id);
        return; // Don't close dialog
      }

      handleCloseDialog();
      fetchProducts();
    } catch (error) {
      console.error("Error saving product:", error);
      toast.error(tProducts("admin.list.saveError"));
    }
  };

  // Handle product deletion
  const handleDeleteProduct = async (id) => {
    if (window.confirm(tProducts("admin.list.confirmDelete"))) {
      try {
        await productService.deleteProduct(id);
        toast.success(tProducts("admin.list.deleteSuccess"));
        fetchProducts();
      } catch (error) {
        console.error("Error deleting product:", error);
        toast.error(tProducts("admin.list.deleteError"));
      }
    }
  };

  // Handle remove variation
  const handleRemoveVariation = async (variationId) => {
    if (window.confirm(tProducts("admin.list.confirmRemoveVariation"))) {
      try {
        await productService.deleteProduct(variationId);
        // Refresh variations list
        fetchVariations(formData.id);
        toast.success(tProducts("admin.list.variationRemoved"));
      } catch (error) {
        console.error("Error removing variation:", error);
        toast.error(tProducts("admin.list.errorRemovingVariation"));
      }
    }
  };

  // Render attribute input based on attribute type
  const renderAttributeInput = (attribute) => {
    // Get selected attributes for variations if this is a variable product
    const getSelectedVariationAttributes = () => {
      if (formData.productType === "variable" && formData.id) {
        try {
          const savedData = localStorage.getItem(
            `variation_attributes_${formData.id}`
          );
          return savedData ? JSON.parse(savedData) : [];
        } catch (error) {
          console.error("Error parsing saved attributes:", error);
          return [];
        }
      }
      return [];
    };

    const selectedVariationAttributes = getSelectedVariationAttributes();

    // Determine if attribute should be disabled for this product type
    const isVariableProduct = formData.productType === "variable";
    const isVariatableAttribute =
      attribute.CategoryAttribute?.isVariantable ||
      attribute.categoryAttribute?.isVariantable;

    // Only disable if it's both variatable AND selected for variations
    const isSelectedForVariation = selectedVariationAttributes.includes(
      attribute.id
    );
    const shouldDisableAttribute =
      isVariableProduct && isVariatableAttribute && isSelectedForVariation;

    // Find the attribute value from formData.attributes array
    const attributeValue =
      formData.attributes.find((attr) => attr.attributeId === attribute.id)
        ?.value || "";

    let helperText = "";
    if (shouldDisableAttribute) {
      helperText = tProducts("admin.list.attributeUsedInVariations");
    } else if (attribute.helperText) {
      helperText = attribute.helperText;
    } else if (
      isVariableProduct &&
      isVariatableAttribute &&
      !isSelectedForVariation
    ) {
      helperText = tProducts("admin.list.canBeUsedForVariations");
    }

    if (attribute.type === "select" || attribute.type === "multiselect") {
      // For multiselect attributes in simple products, treat as single select
      const isMultiSelect = attribute.type === "multiselect";
      const shouldTreatAsSelect =
        isMultiSelect && formData.productType === "simple";

      // For multiselect, collect all values with the same attributeId
      const attributeValues =
        isMultiSelect && !shouldTreatAsSelect
          ? formData.attributes
              .filter((attr) => attr.attributeId === attribute.id)
              .map((attr) => attr.value)
          : [attributeValue];

      return (
        <FormControl
          fullWidth
          margin="normal"
          key={attribute.id}
          disabled={shouldDisableAttribute}
        >
          <InputLabel>{attribute.name}</InputLabel>
          <Select
            name={`attribute-${attribute.id}`}
            value={shouldTreatAsSelect ? attributeValue : attributeValues}
            label={attribute.name}
            onChange={(e) =>
              handleAttributeChange(attribute.id, e.target.value)
            }
            multiple={isMultiSelect && !shouldTreatAsSelect}
            disabled={loadingAttributes || shouldDisableAttribute}
          >
            <MenuItem value="">
              <em>{t("common.none")}</em>
            </MenuItem>
            {attribute.values?.map((val) => (
              <MenuItem key={val.id} value={val.value}>
                {val.value}
              </MenuItem>
            ))}
          </Select>
          {helperText && <FormHelperText>{helperText}</FormHelperText>}
        </FormControl>
      );
    } else if (attribute.type === "text") {
      return (
        <TextField
          fullWidth
          margin="normal"
          key={attribute.id}
          name={`attribute-${attribute.id}`}
          label={attribute.name}
          value={attributeValue}
          onChange={(e) => handleAttributeChange(attribute.id, e.target.value)}
          disabled={loadingAttributes || shouldDisableAttribute}
          helperText={helperText}
        />
      );
    } else if (attribute.type === "number") {
      return (
        <TextField
          fullWidth
          margin="normal"
          key={attribute.id}
          name={`attribute-${attribute.id}`}
          label={attribute.name}
          type="number"
          value={attributeValue}
          onChange={(e) => handleAttributeChange(attribute.id, e.target.value)}
          disabled={loadingAttributes || shouldDisableAttribute}
          helperText={helperText}
        />
      );
    } else if (attribute.type === "boolean") {
      return (
        <FormControl
          fullWidth
          margin="normal"
          key={attribute.id}
          disabled={shouldDisableAttribute}
        >
          <FormControlLabel
            control={
              <Switch
                checked={attributeValue === "true" || attributeValue === true}
                onChange={(e) =>
                  handleAttributeChange(
                    attribute.id,
                    e.target.checked.toString()
                  )
                }
                disabled={loadingAttributes || shouldDisableAttribute}
              />
            }
            label={attribute.name}
          />
          {helperText && <FormHelperText>{helperText}</FormHelperText>}
        </FormControl>
      );
    }

    return null;
  };

  // Add effect to load variations when product is selected
  useEffect(() => {
    if (formData?.id && formData.productType === "variable") {
      fetchVariations(formData.id);
    } else {
      setVariations([]);
    }
  }, [formData?.id, formData?.productType]);

  // Add a specific handler for the success of variation generation
  const handleVariationGenerationSuccess = (response) => {
    console.log("Variation generation success:", response);
    toast.success(tProducts("admin.list.variationSuccess"));
    fetchVariations(formData.id);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        {tProducts("admin.list.title")}
      </Typography>

      {/* Search and Add Product */}
      <Box sx={{ display: "flex", mb: 2, justifyContent: "space-between" }}>
        <TextField
          label={tProducts("catalog.search.placeholder")}
          variant="outlined"
          size="small"
          value={searchQuery}
          onChange={handleSearch}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1 }} />,
          }}
          sx={{ width: 300 }}
        />
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleNavigateToCreate}
        >
          {tProducts("admin.list.addProduct")}
        </Button>
      </Box>

      {/* Products Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width="60px">{t("common.image")}</TableCell>
              <TableCell>{tProducts("admin.list.columns.name")}</TableCell>
              <TableCell>{tProducts("admin.list.columns.sku")}</TableCell>
              <TableCell>{tProducts("admin.list.columns.price")}</TableCell>
              <TableCell>{tProducts("admin.list.columns.stock")}</TableCell>
              <TableCell>{tProducts("admin.list.columns.category")}</TableCell>
              <TableCell>{tProducts("admin.list.columns.type")}</TableCell>
              <TableCell align="right">{t("common.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : products.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  {tProducts("admin.list.noProducts")}
                </TableCell>
              </TableRow>
            ) : (
              products.map((product) => (
                <TableRow key={product.id}>
                  <TableCell>
                    <Box sx={{ position: "relative" }}>
                      <Box
                        component="img"
                        src={
                          getDirectImageUrl(product.imageUrl) ||
                          "/placeholder.svg"
                        }
                        alt={product.name}
                        sx={{
                          width: 50,
                          height: 50,
                          objectFit: "contain",
                          borderRadius: 1,
                          border: "1px solid #eee",
                        }}
                      />
                      {product.images && product.images.length > 0 && (
                        <Chip
                          label={product.images.length}
                          color="primary"
                          size="small"
                          sx={{
                            position: "absolute",
                            bottom: -8,
                            right: -8,
                            height: 20,
                            minWidth: 20,
                            fontSize: "0.7rem",
                          }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {product.name}
                    {product.parentId && (
                      <Chip
                        label={tProducts("admin.list.variation")}
                        color="info"
                        size="small"
                        sx={{ ml: 1 }}
                      />
                    )}
                    {product.productType === "variable" && (
                      <Chip
                        label={tProducts("admin.list.variableProduct")}
                        color="secondary"
                        size="small"
                        sx={{ ml: 1 }}
                      />
                    )}
                  </TableCell>
                  <TableCell>{product.sku}</TableCell>
                  <TableCell>
                    {(() => {
                      if (product.productType === "variable") {
                        // Try new variationSummary format first
                        if (product.variationSummary) {
                          return `${formatCurrency(
                            parseFloat(product.variationSummary.minPrice)
                          )} - ${formatCurrency(
                            parseFloat(product.variationSummary.maxPrice)
                          )}`;
                        }

                        // Fallback to legacy variations format
                        if (product.variations?.length > 0) {
                          const prices = product.variations.map(
                            (v) => parseFloat(v.price) || 0
                          );
                          return `${formatCurrency(
                            Math.min(...prices)
                          )} - ${formatCurrency(Math.max(...prices))}`;
                        }

                        // No variations data available
                        return formatCurrency(parseFloat(product.price) || 0);
                      }

                      // Simple product
                      return formatCurrency(parseFloat(product.price) || 0);
                    })()}
                  </TableCell>
                  <TableCell>
                    {(() => {
                      if (product.productType === "variable") {
                        // Try new variationSummary format first
                        if (product.variationSummary) {
                          return product.variationSummary.totalStock;
                        }

                        // Fallback to legacy variations format
                        if (product.variations?.length > 0) {
                          return product.variations.reduce(
                            (total, variation) =>
                              total + (parseInt(variation.stock) || 0),
                            0
                          );
                        }

                        // No variations data available
                        return product.stock || 0;
                      }

                      // Simple product
                      return product.stock || 0;
                    })()}
                  </TableCell>
                  <TableCell>{product.category?.name}</TableCell>
                  <TableCell>
                    <Chip
                      label={
                        product.isActive
                          ? t("common.active")
                          : t("common.inactive")
                      }
                      color={product.isActive ? "success" : "default"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      color="primary"
                      onClick={() => handleNavigateToEdit(product.id)}
                      size="small"
                      title={tProducts("admin.list.editProduct")}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => handleDeleteProduct(product.id)}
                      size="small"
                      title={tProducts("admin.list.deleteProduct")}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalProducts}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      {/* Product Form Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {isEditing
            ? tProducts("admin.list.editProduct")
            : tProducts("admin.list.addProduct")}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
            <Tabs
              value={formTab}
              onChange={(e, newValue) => {
                // Only update if the value is different
                if (newValue !== formTab) {
                  setFormTab(newValue);
                }
              }}
              indicatorColor="primary"
              textColor="primary"
              variant="fullWidth"
              sx={{ mb: 2 }}
            >
              <Tab label={tProducts("admin.list.basicInfo")} />
              <Tab label={tProducts("admin.list.attributes")} />
              <Tab
                label={tProducts("admin.list.images")}
                icon={<ImageIcon />}
              />
              {formData.productType === "variable" && (
                <Tab label={tProducts("admin.list.variations")} />
              )}
            </Tabs>
          </Box>

          {/* Basic Info Tab */}
          <TabPanel value={formTab} index={0}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={tProducts("admin.list.name")}
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  error={!!formErrors.name}
                  helperText={formErrors.name}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={tProducts("admin.list.sku")}
                  name="sku"
                  value={formData.sku}
                  onChange={handleInputChange}
                  error={!!formErrors.sku}
                  helperText={formErrors.sku}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>{tProducts("admin.list.productType")}</InputLabel>
                  <Select
                    name="productType"
                    value={formData.productType || "simple"}
                    onChange={handleInputChange}
                    label={tProducts("admin.list.productType")}
                  >
                    <MenuItem value="simple">
                      {tProducts("admin.list.simpleProduct")}
                    </MenuItem>
                    <MenuItem value="variable">
                      {tProducts("admin.list.variableProduct")}
                    </MenuItem>
                  </Select>
                  <FormHelperText>
                    {formData.productType === "variable"
                      ? tProducts("admin.list.variableProductHelp")
                      : tProducts("admin.list.simpleProductHelp")}
                  </FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={tProducts("admin.list.description")}
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  multiline
                  rows={3}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={tProducts("admin.list.price")}
                  name="price"
                  type="number"
                  value={formData.price}
                  onChange={handleInputChange}
                  error={!!formErrors.price}
                  helperText={
                    formData.productType === "variable"
                      ? tProducts("admin.list.variablePriceHelp")
                      : formErrors.price
                  }
                  InputProps={{ startAdornment: "CHF" }}
                  required={formData.productType !== "variable"}
                  disabled={formData.productType === "variable"}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={tProducts("admin.list.salePrice")}
                  name="salePrice"
                  type="number"
                  value={formData.salePrice}
                  onChange={handleInputChange}
                  error={!!formErrors.salePrice}
                  helperText={
                    formData.productType === "variable"
                      ? tProducts("admin.list.variablePriceHelp")
                      : formErrors.salePrice
                  }
                  InputProps={{ startAdornment: "CHF" }}
                  disabled={formData.productType === "variable"}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={tProducts("admin.list.cost")}
                  name="cost"
                  type="number"
                  value={formData.cost}
                  onChange={handleInputChange}
                  error={!!formErrors.cost}
                  helperText={
                    formData.productType === "variable"
                      ? tProducts("admin.list.variablePriceHelp")
                      : formErrors.cost
                  }
                  InputProps={{ startAdornment: "CHF" }}
                  disabled={formData.productType === "variable"}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={tProducts("admin.list.stock")}
                  name="stock"
                  type="number"
                  value={formData.stock}
                  onChange={handleInputChange}
                  error={!!formErrors.stock}
                  helperText={
                    formData.productType === "variable"
                      ? tProducts("admin.list.variableStockHelp")
                      : formErrors.stock
                  }
                  disabled={formData.productType === "variable"}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={tProducts("admin.list.weight")}
                  name="weight"
                  type="number"
                  value={formData.weight}
                  onChange={handleInputChange}
                  error={!!formErrors.weight}
                  helperText={
                    formData.productType === "variable"
                      ? tProducts("admin.list.variableWeightHelp")
                      : formErrors.weight
                  }
                  disabled={formData.productType === "variable"}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={tProducts("admin.list.dimensions")}
                  name="dimensions"
                  value={formData.dimensions}
                  onChange={handleInputChange}
                  placeholder="L x W x H"
                  helperText={
                    formData.productType === "variable"
                      ? tProducts("admin.list.variableDimensionsHelp")
                      : ""
                  }
                  disabled={formData.productType === "variable"}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth error={!!formErrors.categoryId} required>
                  <InputLabel>{tProducts("admin.list.category")}</InputLabel>
                  <Select
                    name="categoryId"
                    value={formData.categoryId}
                    label={tProducts("admin.list.category")}
                    onChange={handleInputChange}
                  >
                    {categories.map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {formErrors.categoryId && (
                    <FormHelperText>{formErrors.categoryId}</FormHelperText>
                  )}
                </FormControl>
                <Box
                  sx={{ mt: 1, display: "flex", justifyContent: "flex-end" }}
                >
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => window.open("/admin/categories", "_blank")}
                  >
                    {tProducts("admin.list.addCategory")}
                  </Button>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={tProducts("admin.list.imageUrl")}
                  name="imageUrl"
                  value={formData.imageUrl}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      name="isActive"
                      checked={formData.isActive}
                      onChange={handleInputChange}
                    />
                  }
                  label={tProducts("admin.list.isActive")}
                />
              </Grid>
            </Grid>
          </TabPanel>

          {/* Attributes Tab */}
          <TabPanel value={formTab} index={1}>
            {loadingAttributes ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  p: 3,
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography variant="subtitle1">
                    {categoryAttributes.length === 0
                      ? tProducts("admin.list.noCategoryAttributes")
                      : tProducts("admin.list.availableAttributes")}
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => window.open("/admin/attributes", "_blank")}
                  >
                    {tProducts("admin.list.manageAttributes")}
                  </Button>
                </Box>

                {categoryAttributes.length === 0 ? (
                  <Typography color="textSecondary" sx={{ mb: 2 }}>
                    {tProducts("admin.list.addAttributesToCategory")}
                  </Typography>
                ) : (
                  <Grid container spacing={2}>
                    {categoryAttributes.map((catAttr) => {
                      // Get the attribute from the catAttr object itself since it contains the attribute data
                      const attribute = catAttr;
                      if (!attribute) return null;

                      // Get the CategoryAttribute data which contains isRequired and isVariantable
                      const categoryAttributeData =
                        catAttr.CategoryAttribute ||
                        catAttr.categoryAttribute ||
                        {};

                      // For variable products, identify variatable attributes
                      const isVariatableAttribute =
                        categoryAttributeData.isVariantable === true;
                      const isVariableProduct =
                        formData.productType === "variable";

                      // Find if this product has a value for this attribute
                      const productAttr = formData.attributes.find(
                        (a) => a.attributeId === attribute.id
                      );

                      return (
                        <Grid item xs={12} sm={6} key={attribute.id}>
                          <Box sx={{ mb: 1, display: "flex", gap: 1 }}>
                            {categoryAttributeData.isRequired && (
                              <Chip
                                label={t("common.required")}
                                color="primary"
                                size="small"
                              />
                            )}
                            {isVariatableAttribute && (
                              <Chip
                                label={tProducts("admin.list.isVariantable")}
                                color="secondary"
                                size="small"
                              />
                            )}
                          </Box>
                          {renderAttributeInput(attribute)}
                        </Grid>
                      );
                    })}
                  </Grid>
                )}
              </>
            )}
          </TabPanel>

          {/* Images Tab - Only visible when editing */}
          <TabPanel value={formTab} index={2}>
            {isEditing ? (
              <ProductImageManager
                productId={formData.id}
                onImagesChange={handleImagesChange}
              />
            ) : (
              <Box sx={{ p: 3, textAlign: "center" }}>
                <Typography variant="body1" gutterBottom>
                  {tProducts("admin.list.saveProductFirst")}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {tProducts("admin.list.imageUploadAfterSave")}
                </Typography>
              </Box>
            )}
          </TabPanel>

          {/* Variations Tab */}
          {formData.productType === "variable" && (
            <TabPanel value={formTab} index={3}>
              <Box>
                {isEditing ? (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    {tProducts("admin.list.saveBeforeVariations")}
                  </Alert>
                ) : (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    {tProducts("admin.list.onlyVariableCanHaveVariations")}
                  </Alert>
                )}
              </Box>
              <Box>
                {/* Variation Generator */}
                <VariationGenerator
                  productId={formData.id}
                  categoryAttributes={categoryAttributes}
                  loading={loadingAttributes || loadingVariations}
                  onSuccess={handleVariationGenerationSuccess}
                />

                <Divider sx={{ my: 3 }} />

                {/* Existing variations list */}
                <Typography variant="h6" gutterBottom>
                  {tProducts("admin.list.existingVariations")}
                </Typography>

                {variations.length === 0 ? (
                  <Alert severity="info">
                    {tProducts("admin.list.noVariationsYet")}
                  </Alert>
                ) : (
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>{tProducts("admin.list.name")}</TableCell>
                          <TableCell>{tProducts("admin.list.sku")}</TableCell>
                          <TableCell>{tProducts("admin.list.price")}</TableCell>
                          <TableCell>{tProducts("admin.list.stock")}</TableCell>
                          <TableCell>{t("common.actions")}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {variations.map((variation) => (
                          <TableRow key={variation.id}>
                            <TableCell>{variation.name}</TableCell>
                            <TableCell>{variation.sku}</TableCell>
                            <TableCell>
                              {formatCurrency(variation.price)}
                            </TableCell>
                            <TableCell>{variation.stock}</TableCell>
                            <TableCell>
                              <Button
                                size="small"
                                onClick={() =>
                                  handleOpenEditDialog(variation.id)
                                }
                              >
                                {t("common.edit")}
                              </Button>
                              <Button
                                size="small"
                                color="error"
                                onClick={() =>
                                  handleRemoveVariation(variation.id)
                                }
                              >
                                {t("common.delete")}
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            </TabPanel>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t("buttons.cancel")}</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {isEditing ? t("common.update") : t("common.create")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminProductsPage;
